import { computed, onMounted, watch } from 'vue'
import { stripRichText } from '@/utils/richText'

export interface SEOConfig {
  title: string
  description: string
  keywords: string
  ogImage?: string
  canonical?: string
  noindex?: boolean
  siteName?: string
  applicationName?: string
  twitterUrl?: string
  author?: string
  structuredData?: any
}

export interface ProductMeta {
  id: string
  name: string
  description?: string
  price: number
  currency: string
  availability: 'InStock' | 'OutOfStock'
  category: string
  image?: string
  sku?: string
}

const pageConfigs: Record<string, SEOConfig> = {
  home: {
    title: 'Venmas | POS, Inventory, M-Pesa & eTIMS for Kenya',
    description: 'Run sales, inventory, M-Pesa payments, eTIMS invoicing and an online storefront with Venmas, built for Kenyan retailers.',
    keywords: 'Kenya POS, inventory management, M-Pesa retail, eTIMS invoicing, online storefront',
    canonical: 'https://venmas.store/', siteName: 'Venmas', ogImage: 'https://venmas.store/og-image.png'
  },
  features: { title: 'Retail POS, Inventory, M-Pesa & eTIMS Features | Venmas', description: 'Explore Venmas features for Kenyan retailers: POS, inventory, M-Pesa, eTIMS, online storefronts, reporting and staff controls.', keywords: 'retail POS Kenya, inventory, M-Pesa, eTIMS, storefront', canonical: 'https://venmas.store/features', siteName: 'Venmas' },
  pricing: { title: 'Venmas Pricing | Retail Management Plans for Kenya', description: 'Compare current Venmas plans for POS, inventory, M-Pesa, eTIMS and online storefront tools for Kenyan retailers.', keywords: 'Venmas pricing, POS pricing Kenya, retail software plans', canonical: 'https://venmas.store/pricing', siteName: 'Venmas' },
  solutions: { title: 'Retail Business Solutions for Kenya | Venmas', description: 'See how Venmas supports general retail, wines and spirits, agrovets, boutiques, electronics shops and hardware stores.', keywords: 'retail solutions Kenya, agrovet POS, boutique POS, hardware POS', canonical: 'https://venmas.store/solutions', siteName: 'Venmas' },
  help: { title: 'Venmas Help Centre | Setup, Sales & Storefront Support', description: 'Get help setting up Venmas, managing sales and inventory, receiving payments and publishing your online storefront.', keywords: 'Venmas help, retailer support, storefront support', canonical: 'https://venmas.store/help', siteName: 'Venmas' },
  contact: { title: 'Contact Venmas | Retailer Sales & Support Kenya', description: 'Contact Venmas for product questions, retailer onboarding and technical support in Kenya.', keywords: 'contact Venmas, Venmas support Kenya', canonical: 'https://venmas.store/contact', siteName: 'Venmas' },
  security: { title: 'Security at Venmas | Protecting Retail Business Data', description: 'Learn about the practices Venmas uses to protect retailer accounts, operational data and platform access.', keywords: 'Venmas security, retail data protection', canonical: 'https://venmas.store/security', siteName: 'Venmas' },
  catalog: {
    title: 'Product Catalog | Venmas Retailers',
    description: 'Browse products from local Kenyan retailers with prices in KES and shop through their Venmas storefronts.',
    keywords: 'product catalog, local retailers, prices KES, Kenyan stores',
    canonical: 'https://venmas.store/catalog', siteName: 'Venmas'
  },
  faq: { title: 'Frequently Asked Questions | Venmas', description: 'Answers to common questions about Venmas setup, point of sale, inventory, M-Pesa payments and staff access.', keywords: 'Venmas FAQ, POS questions, M-Pesa help', canonical: 'https://venmas.store/faq', siteName: 'Venmas' },
  'whatsapp-support': { title: 'Venmas WhatsApp Support | Contact Our Team', description: 'Contact Venmas support on WhatsApp for help with your account, sales, stock and storefront.', keywords: 'Venmas WhatsApp support, retailer help', canonical: 'https://venmas.store/whatsapp-support', siteName: 'Venmas' },
  privacy: { title: 'Privacy Policy | Venmas', description: 'Read how Venmas handles retailer account details, business data, payment information and support communications.', keywords: 'Venmas privacy policy, data protection', canonical: 'https://venmas.store/privacy', siteName: 'Venmas' },
  terms: { title: 'Terms of Service | Venmas', description: 'Read the Venmas terms for accounts, subscriptions, acceptable use and business data ownership.', keywords: 'Venmas terms of service', canonical: 'https://venmas.store/terms', siteName: 'Venmas' },
  'store-directory': {
    title: 'Shop Local Businesses in Kenya | Venmas Stores',
    description: 'Discover verified Venmas retailers and browse their online storefronts, products and services across Kenya.',
    keywords: 'Kenya online stores, local retailers, Venmas stores, shop local Kenya, retailer directory',
    canonical: 'https://venmas.store/stores',
    ogImage: 'https://venmas.store/og-image.png',
    siteName: 'Venmas'
  },
  login: {
    title: 'Login - Retailer Management System',
    description: 'Secure login for vendors and staff to access the retail management system. Manage your inventory, sales, and orders with M-Pesa integration.',
    keywords: 'login, vendor access, retail management, secure authentication, pos access'
  },
  register: {
    title: 'Register - Join Retailer Management System',
    description: 'Register your retail business with our comprehensive retail management platform. Start managing inventory, processing sales, and accepting M-Pesa payments today.',
    keywords: 'register, sign up, vendor registration, retail business, pos system setup, m-pesa integration'
  },
  about: {
    title: 'About Venmas | Retail Technology Built for Kenya',
    description: 'Learn why Venmas brings sales, stock, payments, compliance and online storefronts together for Kenyan retailers.',
    keywords: 'about Venmas, retail technology Kenya', canonical: 'https://venmas.store/about', siteName: 'Venmas'
  },
  dashboard: {
    title: 'Admin Dashboard - Retailer Management System',
    description: 'Comprehensive admin dashboard for managing your retail business. View sales analytics, inventory alerts, and business metrics in real-time.',
    keywords: 'admin dashboard, sales analytics, business metrics, inventory alerts, retail management',
    noindex: true
  },
  pos: {
    title: 'Point of Sale - Retailer Management System',
    description: 'Modern POS system for processing walk-in and delivery orders with M-Pesa integration and real-time inventory checking.',
    keywords: 'point of sale, pos system, m-pesa payments, order processing, inventory checking',
    noindex: true
  },
  inventory: {
    title: 'Inventory Management - Retailer Management System',
    description: 'Comprehensive inventory management with stock tracking, expiry date monitoring, and automated low-stock alerts.',
    keywords: 'inventory management, stock tracking, expiry dates, low stock alerts, product management',
    noindex: true
  },
  products: {
    title: 'Product Management - Retailer Management System',
    description: 'Manage your product catalog with pricing, categories, and stock levels. Add new products and update existing inventory.',
    keywords: 'product management, catalog management, pricing, categories, stock levels',
    noindex: true
  },
  categories: {
    title: 'Category Management - Retailer Management System',
    description: 'Organize your products into categories for better inventory management and customer browsing experience.',
    keywords: 'category management, product organization, inventory categories',
    noindex: true
  },
  orders: {
    title: 'Order Management - Retailer Management System',
    description: 'View and manage customer orders, track delivery status, and process payments with integrated M-Pesa support.',
    keywords: 'order management, customer orders, delivery tracking, payment processing',
    noindex: true
  },
  reports: {
    title: 'Sales Reports & Analytics - Retailer Management System',
    description: 'Comprehensive sales reporting with analytics, trends, and exportable data for business insights and planning.',
    keywords: 'sales reports, analytics, business insights, trends, data export',
    noindex: true
  },
  settings: {
    title: 'System Settings - Retailer Management System',
    description: 'Configure your retail management system settings, user permissions, and business preferences.',
    keywords: 'system settings, configuration, user permissions, business settings',
    noindex: true
  }
}

export function useSEO(routeName?: string) {
  const getPageConfig = (name?: string): SEOConfig => {
    if (!name) return pageConfigs.home as SEOConfig
    return (pageConfigs as Record<string, SEOConfig>)[name] || pageConfigs.home as SEOConfig
  }

  const config = computed(() => getPageConfig(routeName))

  const updateMetaTag = (name: string, content: string, attribute: 'name' | 'property' = 'name') => {
    let meta = document.querySelector(`meta[${attribute}="${name}"]`)
    if (!meta) {
      meta = document.createElement('meta')
      meta.setAttribute(attribute, name)
      document.head.appendChild(meta)
    }
    meta.setAttribute('content', content)
  }

  const updateLinkTag = (rel: string, href: string) => {
    let link = document.querySelector(`link[rel="${rel}"]`)
    if (!link) {
      link = document.createElement('link')
      link.setAttribute('rel', rel)
      document.head.appendChild(link)
    }
    link.setAttribute('href', href)
  }

  const updateSEO = (customConfig?: Partial<SEOConfig>) => {
    const finalConfig = { ...config.value, ...customConfig }
    
    // Update title
    document.title = finalConfig.title
    
    // Update meta tags
    updateMetaTag('description', finalConfig.description)
    updateMetaTag('keywords', finalConfig.keywords)
    updateMetaTag('robots', finalConfig.noindex ? 'noindex, nofollow' : 'index, follow')
    updateMetaTag('author', finalConfig.author || finalConfig.siteName || 'Venmas Retailer System')
    updateMetaTag('application-name', finalConfig.applicationName || finalConfig.siteName || 'Venmas Retailer System')
    
    // Open Graph
    updateMetaTag('og:title', finalConfig.title, 'property')
    updateMetaTag('og:description', finalConfig.description, 'property')
    updateMetaTag('og:url', finalConfig.canonical || window.location.href, 'property')
    updateMetaTag('og:image', finalConfig.ogImage || 'https://venmas.store/og-image.png', 'property')
    updateMetaTag('og:site_name', finalConfig.siteName || finalConfig.applicationName || 'Venmas Retailer System', 'property')
    
    // Twitter
    updateMetaTag('twitter:card', 'summary_large_image', 'property')
    updateMetaTag('twitter:url', finalConfig.twitterUrl || finalConfig.canonical || window.location.href, 'property')
    updateMetaTag('twitter:title', finalConfig.title, 'property')
    updateMetaTag('twitter:description', finalConfig.description, 'property')
    updateMetaTag('twitter:image', finalConfig.ogImage || 'https://venmas.store/og-image.png', 'property')
    
    // Canonical URL
    if (finalConfig.canonical) {
      updateLinkTag('canonical', finalConfig.canonical)
    }
  }

  const updateProductSEO = (product: ProductMeta) => {
    const productDescription = stripRichText(product.description) || `Buy ${product.name} from Venmas.`

    const productConfig: SEOConfig = {
      title: `${product.name} - ${product.category} | Venmas`,
      description: `${productDescription} Available for ${product.currency} ${product.price}. ${product.availability === 'InStock' ? 'In stock and ready for delivery' : 'Currently out of stock'}.`,
      keywords: `${product.name.toLowerCase()}, ${product.category.toLowerCase()}, ${product.currency.toLowerCase()}, kenyan retail, product catalog, ${product.sku || ''}`,
      ogImage: product.image || 'https://venmas.store/og-image.png',
      canonical: `https://venmas.store/products/${product.id}`,
      structuredData: {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": product.name,
        "description": stripRichText(product.description) || `${product.name} - ${product.category}`,
        "sku": product.sku || product.id,
        "category": product.category,
        "image": product.image ? [product.image] : undefined,
        "offers": {
          "@type": "Offer",
          "price": product.price,
          "priceCurrency": product.currency,
          "availability": `https://schema.org/${product.availability}`,
          "seller": {
            "@type": "Organization",
            "name": "Venmas"
          }
        }
      }
    }
    
    updateSEO(productConfig)
  }

  const updateCategorySEO = (categoryName: string, productCount: number) => {
    const categoryConfig: SEOConfig = {
      title: `${categoryName} - Product Category | Venmas`,
      description: `Browse ${productCount} products in ${categoryName.toLowerCase()} with competitive KES pricing and ordering in Kenya.`,
      keywords: `${categoryName.toLowerCase()}, product category, kenyan ${categoryName.toLowerCase()}, wholesale ${categoryName.toLowerCase()}, retail ${categoryName.toLowerCase()}`,
      canonical: `https://venmas.store/catalog?category=${encodeURIComponent(categoryName.toLowerCase())}`
    }
    
    updateSEO(categoryConfig)
  }

  const addStructuredData = (data: any, id: string = 'page-structured-data') => {
    // Remove existing structured data
    const existing = document.getElementById(id)
    if (existing) {
      existing.remove()
    }

    // Add new structured data
    const script = document.createElement('script')
    script.id = id
    script.type = 'application/ld+json'
    script.textContent = JSON.stringify(data, null, 2)
    document.head.appendChild(script)
  }

  return {
    config,
    updateSEO,
    updateProductSEO,
    updateCategorySEO,
    addStructuredData,
    pageConfigs
  }
}
